import React, { useState, useEffect } from 'react';
import { TargetSelect } from './TargetSelect';

const sevColor = (s) => ({
  critical: 'border-red-600 bg-red-50',
  high: 'border-orange-500 bg-orange-50',
  medium: 'border-yellow-500 bg-yellow-50',
  low: 'border-blue-500 bg-blue-50',
  info: 'border-gray-400 bg-gray-50',
}[(s || '').toLowerCase()] || 'border-gray-300 bg-gray-50');

const sevBadge = (s) => ({
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-white',
  low: 'bg-blue-500 text-white',
  info: 'bg-gray-400 text-white',
}[(s || '').toLowerCase()] || 'bg-gray-300 text-gray-800');

const scoreColor = (score) => {
  if (score >= 85) return { text: 'text-green-700', badge: 'bg-green-500', bg: 'bg-green-50', border: 'border-green-500' };
  if (score >= 60) return { text: 'text-yellow-700', badge: 'bg-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-500' };
  if (score >= 35) return { text: 'text-orange-700', badge: 'bg-orange-500', bg: 'bg-orange-50', border: 'border-orange-500' };
  return { text: 'text-red-700', badge: 'bg-red-500', bg: 'bg-red-50', border: 'border-red-500' };
};

const WebHygieneScanner = () => {
  const [url, setUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('form');
  const [scans, setScans] = useState([]);

  useEffect(() => { fetchScans(); }, []);

  const fetchScans = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch('/api/scans/web-hygiene', { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setScans(await r.json());
    } catch (e) { console.error(e); }
  };

  const handleScan = async () => {
    if (!url) { setError('Por favor seleccioná un objetivo autorizado'); return; }
    setScanning(true); setError(null); setResults(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch('/api/scans/web-hygiene', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url, max_pages: 8 }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Error ejecutando escaneo'); }
      const data = await r.json();
      setResults(data.results);
      setViewMode('results');
      fetchScans();
    } catch (e) { setError(e.message); }
    finally { setScanning(false); }
  };

  const openScan = (s) => { setResults(s.results); setViewMode('results'); };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="web-hygiene-scanner">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">🧼 Higiene de Controles Web (SSRF + CSRF)</h1>
        <p className="text-lg text-gray-600">
          Mapea la superficie web y verifica los controles contra CSRF y SSRF. No explota: muestra lo que un
          atacante vería en los primeros minutos.
        </p>
      </div>

      <div className="mb-6 flex gap-4">
        <button onClick={() => setViewMode('form')} data-testid="web-hygiene-tab-form"
          className={`px-6 py-3 rounded-lg font-semibold ${viewMode === 'form' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
          🚀 Nuevo Escaneo
        </button>
        <button onClick={() => setViewMode('history')} data-testid="web-hygiene-tab-history"
          className={`px-6 py-3 rounded-lg font-semibold ${viewMode === 'history' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
          📋 Historial ({scans.length})
        </button>
      </div>

      {viewMode === 'form' && (
        <>
          <div className="mb-8 bg-purple-50 border-l-4 border-purple-500 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-purple-900 mb-2">Qué analiza este scanner</h3>
            <ul className="text-purple-800 text-sm space-y-1">
              <li>• <strong>Discovery:</strong> formularios que modifican datos y parámetros que reciben una URL.</li>
              <li>• <strong>CSRF:</strong> token anti-CSRF por formulario, cookies SameSite y CORS mal configurado.</li>
              <li>• <strong>SSRF (pasivo):</strong> allowlist, IP privadas/metadata cloud, comportamiento del servidor.</li>
              <li className="text-purple-600">• Nota: sin OAST no se confirma SSRF ciego — esos hallazgos quedan con confianza media.</li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="space-y-4">
              <TargetSelect
                label="URL del sitio web *"
                value={url}
                onChange={setUrl}
                types={['domain', 'ip']}
                asUrl
                allowPort
                disabled={scanning}
                helpText="Se escanea el host autorizado. Si la app vive en un puerto alto (paneles internos), indicá el puerto."
                testid="web-hygiene-target-select"
              />
              <button onClick={handleScan} disabled={scanning} data-testid="web-hygiene-start-btn"
                className="w-full px-8 py-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 disabled:bg-gray-400 text-lg">
                {scanning ? '⏳ Analizando higiene web...' : '🔍 Iniciar Análisis'}
              </button>
            </div>
            {error && <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700" data-testid="web-hygiene-error">❌ {error}</div>}
          </div>
        </>
      )}

      {scanning && (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-900">Mapeando superficie y verificando controles...</p>
          <div className="mt-6 text-sm text-gray-500">
            <p>• Descubriendo formularios y parámetros...</p>
            <p>• Verificando token anti-CSRF, SameSite y CORS...</p>
            <p>• Probando controles SSRF (allowlist / IP internas)...</p>
          </div>
        </div>
      )}

      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          {scans.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Todavía no hay escaneos de Higiene Web.</p>
          ) : (
            <div className="space-y-3">
              {scans.map((s) => (
                <button key={s.id} onClick={() => openScan(s)} data-testid={`web-hygiene-history-row`}
                  className="w-full text-left flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50">
                  <div>
                    <div className="font-medium text-gray-900">{s.target}</div>
                    <div className="text-xs text-gray-500">{new Date(s.created_at).toLocaleString('es-AR')}</div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-white font-bold ${scoreColor(s.results?.hygiene_score || 0).badge}`}>
                    {s.results?.hygiene_score ?? '—'}/100
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {viewMode === 'results' && results && (
        <div className="space-y-8" data-testid="web-hygiene-results">
          <button onClick={() => setViewMode('history')} className="text-purple-600 hover:text-purple-800 font-semibold">← Volver al historial</button>

          <div className={`rounded-lg shadow-xl p-8 border-2 ${scoreColor(results.hygiene_score).border} ${scoreColor(results.hygiene_score).bg}`}>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Higiene de Controles Web</h2>
              <div className={`text-8xl font-bold mb-4 ${scoreColor(results.hygiene_score).text}`} data-testid="web-hygiene-score">
                {results.hygiene_score}<span className="text-3xl text-gray-500">/100</span>
              </div>
              <div className={`inline-block px-6 py-3 rounded-full font-bold text-xl ${scoreColor(results.hygiene_score).badge} text-white`}>
                {results.risk_level}
              </div>
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="bg-white/60 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{results.surface?.state_changing_forms ?? 0}</div>
                  <div className="text-sm text-gray-600">Formularios de estado</div>
                </div>
                <div className="bg-white/60 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{results.surface?.url_accepting_params ?? 0}</div>
                  <div className="text-sm text-gray-600">Parámetros con URL</div>
                </div>
                <div className="bg-white/60 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{results.vulnerabilities?.length ?? 0}</div>
                  <div className="text-sm text-gray-600">Hallazgos</div>
                </div>
              </div>
            </div>
          </div>

          {results.summary_pyme && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">🗣️ En criollo</h3>
              <p className="text-sm italic text-gray-500 mb-3">{results.summary_pyme.headline}</p>
              <ul className="space-y-2">
                {(results.summary_pyme.points || []).map((p, i) => (
                  <li key={i} className="text-gray-700 text-sm flex gap-2"><span>•</span><span>{p}</span></li>
                ))}
              </ul>
            </div>
          )}

          {results.vulnerabilities?.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">🛡️ Hallazgos ({results.vulnerabilities.length})</h3>
              <div className="space-y-4">
                {results.vulnerabilities.map((v, i) => (
                  <div key={i} className={`border-l-4 rounded-lg p-4 ${sevColor(v.severity)}`} data-testid={`web-hygiene-finding-${i}`}>
                    <div className="flex justify-between items-start mb-2 gap-3">
                      <h4 className="font-semibold text-gray-900">{v.title}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${sevBadge(v.severity)}`}>{(v.severity || '').toUpperCase()}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2 text-xs">
                      {v.cwe && <span className="font-mono text-purple-700 bg-purple-100 px-2 py-0.5 rounded">{v.cwe}</span>}
                      {v.owasp_category && <span className="font-mono text-blue-700 bg-blue-100 px-2 py-0.5 rounded">{v.owasp_category}</span>}
                      {v.confidence && <span className="text-gray-600 bg-gray-100 px-2 py-0.5 rounded">Confianza: {v.confidence}</span>}
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{v.description}</p>
                    {v.endpoint && <p className="text-xs text-gray-500 mb-1"><strong>Endpoint:</strong> <code className="bg-gray-200 px-1 rounded">{v.endpoint}</code></p>}
                    {v.evidence && <p className="text-xs text-gray-500 mb-2"><strong>Evidencia:</strong> {v.evidence}</p>}
                    {v.remediation && <div className="mt-2 p-3 bg-white/80 rounded border border-gray-200 text-sm text-gray-600"><strong>💡 Remediación:</strong> {v.remediation}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {results.surface?.surface_points?.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">🗺️ Superficie descubierta</h3>
              <div className="space-y-2">
                {results.surface.surface_points.map((sp, i) => (
                  <div key={i} className="flex gap-3 text-sm border-b border-gray-100 pb-2">
                    <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded shrink-0">{sp.method}</span>
                    <div>
                      <div className="text-gray-800">{sp.detail}</div>
                      <code className="text-xs text-gray-500">{sp.url}</code>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {results.confidence_note && (
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded text-sm text-blue-800">
              ℹ️ {results.confidence_note}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WebHygieneScanner;
